import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FileText, LayoutGrid, ListChecks } from "lucide-react";

import { Label } from "../ui/label";
import { Select } from "../ui/select";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "../ui/sidebar";
import { djangoApi } from "../../services/djangoApi";

const AppShellContext = React.createContext(null);

export function useAppShell() {
  const context = React.useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be used within AppShell.");
  }
  return context;
}

const navPrimary = [
  { label: "New Extraction", to: "/newextraction", icon: LayoutGrid },
  { label: "Results", to: "/results", icon: ListChecks },
  { label: "JSON Templates", to: "/json-templates", icon: FileText },
];

const pageMeta = {
  "/newextraction": {
    title: "New Extraction",
  },
  "/results": {
    title: "Results",
  },
  "/json-templates": {
    title: "JSON Templates",
  },
};

export default function AppShell({ apiKey, clientInfo }) {
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");

  useEffect(() => {
    if (!apiKey) return;
    let isMounted = true;
    async function loadOrganizations() {
      try {
        const data = await djangoApi.listOrganizations(apiKey);
        if (!isMounted) return;
        if (Array.isArray(data)) {
          setOrganizations(data);
          if (data.length > 0 && !selectedOrgId) {
            setSelectedOrgId(data[0].id);
          }
        }
      } catch (e) {
        if (!isMounted) return;
        console.error("Failed to load organizations:", e);
      }
    }
    loadOrganizations();
    return () => {
      isMounted = false;
    };
  }, [apiKey, selectedOrgId]);

  const selectedOrg = useMemo(
    () => organizations.find((org) => String(org.id) === String(selectedOrgId)),
    [organizations, selectedOrgId],
  );

  const currentMeta =
    pageMeta[location.pathname] ||
    pageMeta["/newextraction"] || {
      title: "Workspace",
    };

  const contextValue = useMemo(
    () => ({
      organizations,
      selectedOrgId,
      setSelectedOrgId,
      selectedOrg,
      clientInfo,
    }),
    [organizations, selectedOrgId, selectedOrg, clientInfo],
  );

  return (
    <AppShellContext.Provider value={contextValue}>
      <SidebarProvider className="bg-background">
        <Sidebar>
          <SidebarHeader>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-md bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
                OA
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  OCR Platform
                </div>
              </div>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Extraction</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navPrimary.map((item) => (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        asChild
                        variant={
                          location.pathname === item.to ? "active" : "default"
                        }
                        className={item.disabled ? "opacity-60" : undefined}
                      >
                        {item.disabled ? (
                          <button type="button" disabled>
                            <item.icon className="size-4" />
                            <span>{item.label}</span>
                          </button>
                        ) : (
                          <NavLink to={item.to}>
                            <item.icon className="size-4" />
                            <span>{item.label}</span>
                          </NavLink>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            <div className="space-y-1 px-2 text-xs text-muted-foreground">
              <div className="text-sm text-foreground">
                {clientInfo?.client_name || "Client workspace"}
              </div>
              <div>
                {selectedOrg?.name
                  ? `${selectedOrg.name}`
                  : "No tenant selected"}
              </div>
            </div>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset>
          <header className="sticky top-0 z-20 border-b bg-background">
            <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 lg:px-8">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="lg:hidden" />
                <div className="space-y-1">
                  <div className="text-2xl font-semibold text-foreground">
                    {currentMeta.title}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {clientInfo ? (
                  <div className="rounded-md border px-3 py-1 text-xs font-semibold text-muted-foreground">
                    {clientInfo.client_name}
                  </div>
                ) : null}
                {organizations.length > 0 ? (
                  <div className="flex items-center gap-2 rounded-md border px-3 py-1 text-xs font-semibold text-muted-foreground">
                    <Label htmlFor="orgSelect">Tenant</Label>
                    <Select
                      id="orgSelect"
                      value={selectedOrgId}
                      onChange={(e) => setSelectedOrgId(e.target.value)}
                      className="h-7 w-auto border-none bg-transparent px-2 text-xs font-semibold"
                    >
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}
                <div className="rounded-md border px-3 py-1 text-xs font-semibold text-muted-foreground">
                  Production
                </div>
              </div>
            </div>
          </header>
          <main className="flex-1 px-4 pb-10 pt-6 lg:px-8">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </AppShellContext.Provider>
  );
}
